import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Chip,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { inventoryService } from "../../utils/services/inventoryService";
import { formatDate } from "../../utils/formatDate";

/**
 * Per-warehouse inventory for one variant.
 *
 * A SKU does not have "an" inventory row: the inventory collection is unique on
 * sku+client+provider+warehouse, so a SKU stocked in three warehouses has three
 * rows. The Inventory Status tiles above this accordion are the ROLLED-UP total
 * across those rows; this is the breakdown behind the total.
 *
 * It fetches on first expand and never again (per variant), which is the whole
 * reason it is an accordion — a product with 40 variants would otherwise fire 40
 * inventory queries just to open the modal.
 */

const headCellSx = {
  fontFamily: "Poppins",
  fontSize: 11,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.4px",
  color: "var(--text-tertiary)",
  backgroundColor: "var(--bg-secondary)",
  borderBottom: "1px solid var(--border-color)",
  whiteSpace: "nowrap",
  py: 0.75,
};

const cellSx = {
  fontFamily: "Poppins",
  fontSize: 12,
  color: "var(--text-primary)",
  borderBottom: "1px solid var(--border-color)",
  whiteSpace: "nowrap",
  py: 0.75,
};

const numCellSx = {
  ...cellSx,
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
};

const totalCellSx = {
  ...numCellSx,
  fontWeight: 700,
  color: "var(--text-primary)",
  backgroundColor: "var(--bg-secondary)",
};

// The provider-reported states are optional and provider-specific (only Pipe17
// fills them in today), so their columns only appear when some row carries them.
const STATE_COLUMNS = [
  { key: "available", label: "Available" },
  { key: "committed", label: "Committed" },
  { key: "damaged", label: "Damaged" },
  { key: "hold", label: "Hold" },
];

const num = (value) => (typeof value === "number" ? value : 0);

// A state key that the provider never reported is not zero — it is unknown, and
// `states_reported` is what tells the two apart. Rendering "0" for an unreported
// key is exactly the confusion the states_reported field exists to prevent.
const stateValue = (row, key) => {
  const reported = Array.isArray(row?.states_reported)
    ? row.states_reported
    : null;
  if (reported && !reported.includes(key)) return null;
  const value = row?.states?.[key];
  return typeof value === "number" ? value : null;
};

const warehouseLabel = (row) => {
  const wh = row?.warehouse;
  if (!wh) return "Unassigned";
  if (typeof wh === "string") return wh;
  return wh.name || wh.warehouse_code || "Unassigned";
};

const WarehouseInventory = ({ productId, sku }) => {
  const [expanded, setExpanded] = useState(false);
  const [rows, setRows] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const load = useCallback(async () => {
    if (!productId || !sku) return;
    setIsLoading(true);
    setError(null);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const res = await inventoryService.fetchByProductSku(
        productId,
        sku,
        controller.signal,
      );
      setRows(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
      console.error("Error fetching warehouse inventory:", err);
      setError("Could not load warehouse inventory.");
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
        setIsLoading(false);
      }
    }
  }, [productId, sku]);

  // Abort an in-flight request if the modal closes mid-fetch.
  useEffect(() => () => abortRef.current?.abort(), []);

  const handleChange = (_event, isExpanded) => {
    setExpanded(isExpanded);
    // Fetch once per variant: an already-loaded breakdown is reused on re-expand,
    // and a failed one can be retried by collapsing and expanding again.
    if (isExpanded && rows === null && !isLoading) load();
  };

  if (!productId || !sku) return null;

  const activeStateColumns = STATE_COLUMNS.filter(({ key }) =>
    (rows || []).some((row) => stateValue(row, key) !== null),
  );

  const totals = (rows || []).reduce(
    (acc, row) => ({
      quantity: acc.quantity + num(row.quantity),
      in_hand: acc.in_hand + num(row.in_hand),
      in_transit: acc.in_transit + num(row.in_transit),
    }),
    { quantity: 0, in_hand: 0, in_transit: 0 },
  );

  return (
    <Accordion
      expanded={expanded}
      onChange={handleChange}
      disableGutters
      elevation={0}
      TransitionProps={{ unmountOnExit: true }}
      sx={{
        mb: 1.5,
        border: "1px solid var(--border-color)",
        borderRadius: 1,
        background: "var(--bg-secondary)",
        "&:before": { display: "none" },
        "&.Mui-expanded": { margin: 0, mb: 1.5 },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
        sx={{
          minHeight: 40,
          px: 1.5,
          "&.Mui-expanded": { minHeight: 40 },
          "& .MuiAccordionSummary-content": { my: 1 },
          "& .MuiAccordionSummary-content.Mui-expanded": { my: 1 },
        }}
      >
        <Typography
          variant="caption"
          fontFamily="Poppins"
          fontWeight={600}
          color="var(--text-tertiary)"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          <WarehouseIcon sx={{ fontSize: 13, color: "#00796b" }} />
          Warehouse Inventory
          {rows !== null && (
            <Chip
              label={`${rows.length} warehouse${rows.length === 1 ? "" : "s"}`}
              size="small"
              variant="outlined"
              sx={{
                ml: 0.5,
                height: 18,
                fontFamily: "Poppins",
                fontSize: 10,
                borderColor: "var(--border-color)",
                color: "var(--text-secondary)",
              }}
            />
          )}
        </Typography>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 1.5, pt: 0 }}>
        {isLoading && (
          <Stack spacing={0.75}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} variant="rectangular" height={28} />
            ))}
          </Stack>
        )}

        {!isLoading && error && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              p: 1,
              borderRadius: 1,
              background: "var(--bg-paper)",
              border: "1px solid var(--border-color)",
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 16, color: "#d32f2f" }} />
            <Typography
              variant="caption"
              fontFamily="Poppins"
              color="var(--text-secondary)"
            >
              {error} Collapse and expand this section to retry.
            </Typography>
          </Box>
        )}

        {!isLoading && !error && rows !== null && rows.length === 0 && (
          <Typography
            variant="caption"
            fontFamily="Poppins"
            color="var(--text-secondary)"
          >
            No warehouse inventory recorded for this SKU.
          </Typography>
        )}

        {!isLoading && !error && rows !== null && rows.length > 0 && (
          <TableContainer
            sx={{
              overflowX: "auto",
              background: "var(--bg-paper)",
              border: "1px solid var(--border-color)",
              borderRadius: 1,
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>Warehouse</TableCell>
                  <TableCell sx={headCellSx}>Provider</TableCell>
                  <TableCell sx={{ ...headCellSx, textAlign: "right" }}>
                    Total Qty
                  </TableCell>
                  <TableCell sx={{ ...headCellSx, textAlign: "right" }}>
                    In Hand
                  </TableCell>
                  <TableCell sx={{ ...headCellSx, textAlign: "right" }}>
                    In Transit
                  </TableCell>
                  {activeStateColumns.map(({ key, label }) => (
                    <TableCell
                      key={key}
                      sx={{ ...headCellSx, textAlign: "right" }}
                    >
                      {label}
                    </TableCell>
                  ))}
                  <TableCell sx={{ ...headCellSx, textAlign: "right" }}>
                    Last PO Date
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row._id} hover>
                    <TableCell sx={{ ...cellSx, fontWeight: 600 }}>
                      <Tooltip
                        title={row?.warehouse?.warehouse_code || ""}
                        placement="top"
                      >
                        <span>{warehouseLabel(row)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell
                      sx={{ ...cellSx, color: "var(--text-secondary)" }}
                    >
                      {row?.provider?.name || "-"}
                    </TableCell>
                    <TableCell sx={{ ...numCellSx, color: "#1976d2" }}>
                      {num(row.quantity)}
                    </TableCell>
                    <TableCell sx={{ ...numCellSx, color: "#388e3c" }}>
                      {num(row.in_hand)}
                    </TableCell>
                    <TableCell sx={{ ...numCellSx, color: "#f57c00" }}>
                      {num(row.in_transit)}
                    </TableCell>
                    {activeStateColumns.map(({ key }) => {
                      const value = stateValue(row, key);
                      return (
                        <TableCell
                          key={key}
                          sx={{
                            ...numCellSx,
                            color:
                              value === null
                                ? "var(--text-tertiary)"
                                : "var(--text-primary)",
                          }}
                        >
                          {value === null ? "—" : value}
                        </TableCell>
                      );
                    })}
                    <TableCell
                      sx={{ ...numCellSx, color: "var(--text-secondary)" }}
                    >
                      {formatDate(row.last_po_created_date, "N/A")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>

              {rows.length > 1 && (
                <TableFooter>
                  <TableRow>
                    <TableCell
                      sx={{
                        ...cellSx,
                        ...totalCellSx,
                        textAlign: "left",
                      }}
                      colSpan={2}
                    >
                      All warehouses
                    </TableCell>
                    <TableCell sx={totalCellSx}>{totals.quantity}</TableCell>
                    <TableCell sx={totalCellSx}>{totals.in_hand}</TableCell>
                    <TableCell sx={totalCellSx}>{totals.in_transit}</TableCell>
                    {/* Provider states are deliberately not totalled: promise and
                        availability figures are not additive across warehouses
                        (see control-tower models/inventory-states.js). */}
                    {activeStateColumns.map(({ key }) => (
                      <TableCell key={key} sx={totalCellSx}>
                        —
                      </TableCell>
                    ))}
                    <TableCell sx={totalCellSx}>—</TableCell>
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </TableContainer>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default WarehouseInventory;
